import type { Enum } from '@partou/core';
import type {
  Child,
  ChildDevelopment,
  ChildDevelopmentObservation,
  ChildGroup,
  ChildLocation,
  ChildNote,
  ChildObservation,
  ChildrenResult,
  ChildrenQuery,
  ChildSortField,
  DevelopmentDomain,
} from '@partou/my-group/data';
import { joinNames } from '@partou/core';
import {
  MOCK_CHILDREN_CREATE_KEY,
  MOCK_CHILDREN_DELAY_KEY,
  MOCK_CHILDREN_DELETE_KEY,
  MOCK_CHILDREN_HIDE_KEY,
  MOCK_CHILDREN_LOAD_KEY,
  MOCK_CHILDREN_REPORT_KEY,
  MOCK_CHILDREN_SCENARIO_KEY,
} from './mock-keys.const';

export function getMockChildrenDelayMs(): number {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_DELAY_KEY);
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export const MOCK_CHILDREN_LOAD_OUTCOMES = ['success', 'error'] as const;

export type MockChildrenLoadOutcome = Enum<typeof MOCK_CHILDREN_LOAD_OUTCOMES>;

export function getMockChildrenLoadOutcome(): MockChildrenLoadOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_LOAD_KEY);
  return stored === 'error' ? 'error' : 'success';
}

export const MOCK_CHILDREN_DELETE_OUTCOMES = ['success', 'error'] as const;

export type MockChildrenDeleteOutcome = Enum<typeof MOCK_CHILDREN_DELETE_OUTCOMES>;

export function getMockChildrenDeleteOutcome(): MockChildrenDeleteOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_DELETE_KEY);
  return stored === 'error' ? 'error' : 'success';
}

export const MOCK_CHILDREN_REPORT_OUTCOMES = ['success', 'error'] as const;

export type MockChildrenReportOutcome = Enum<typeof MOCK_CHILDREN_REPORT_OUTCOMES>;

export function getMockChildrenReportOutcome(): MockChildrenReportOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_REPORT_KEY);
  return stored === 'error' ? 'error' : 'success';
}

export const MOCK_CHILDREN_CREATE_OUTCOMES = ['success', 'error'] as const;

export type MockChildrenCreateOutcome = Enum<typeof MOCK_CHILDREN_CREATE_OUTCOMES>;

export function getMockChildrenCreateOutcome(): MockChildrenCreateOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_CREATE_KEY);
  return stored === 'error' ? 'error' : 'success';
}

export const MOCK_CHILDREN_HIDE_OUTCOMES = ['success', 'error'] as const;

export type MockChildrenHideOutcome = Enum<typeof MOCK_CHILDREN_HIDE_OUTCOMES>;

export function getMockChildrenHideOutcome(): MockChildrenHideOutcome {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_HIDE_KEY);
  return stored === 'error' ? 'error' : 'success';
}

const MOCK_REPORT_PDF: Readonly<string> = [
  '%PDF-1.4',
  '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
  '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
  '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 200 200]>>endobj',
  'trailer<</Root 1 0 R>>',
  '%%EOF',
].join('\n');

export function buildMockReportPdf(): Blob {
  return new Blob([MOCK_REPORT_PDF], { type: 'application/pdf' });
}

const LOCATIONS = {
  kerkstraat: { id: 'loc-1', name: 'Kerkstraat 45' },
  singel: { id: 'loc-2', name: 'Singel 25' },
  prinsHendrikkade: { id: 'loc-3', name: 'Prins Hendrikkade 50' },
  westerstraat: { id: 'loc-4', name: 'Westerstraat 99' },
  keizersgracht: { id: 'loc-5', name: 'Keizersgracht 123' },
  vondelparkweg: { id: 'loc-6', name: 'Vondelparkweg 8' },
} as const satisfies Record<string, ChildLocation>;

const GROUPS = {
  vlindertjes: { id: 'grp-1', name: 'De Vlindertjes' },
  bijtjes: { id: 'grp-2', name: 'De Bijtjes' },
  leeuwtjes: { id: 'grp-3', name: 'De Leeuwtjes' },
  kikkertjes: { id: 'grp-4', name: 'De Kikkertjes' },
  vogeltjes: { id: 'grp-5', name: 'De Vogeltjes' },
  olifantjes: { id: 'grp-6', name: 'De Olifantjes' },
  honden: { id: 'grp-7', name: 'De Honden' },
  katjes: { id: 'grp-8', name: 'De Katjes' },
  // grp-15 sits past the first page of group filter options (see mock-groups.const.ts), so a
  // child placed here exercises restoring a selection that was paginated out of the options.
  maantjes: { id: 'grp-15', name: 'De Maantjes' },
} as const satisfies Record<string, ChildGroup>;

const NO_DEVELOPMENT: ChildDevelopment = { observations: [] };

const NO_NOTES: ReadonlyArray<ChildNote> = [];

const NO_OBSERVATIONS: ReadonlyArray<ChildObservation> = [];

const femkeObservationList: ReadonlyArray<ChildObservation> = [
  {
    id: 'obs-femke-1',
    origin: 'automatic',
    status: 'completed',
    dueDate: '2025-10-24',
    filledInBy: 'Ria de Wit',
    completedAt: '2025-10-12',
  },
  {
    id: 'obs-femke-2',
    origin: 'automatic',
    status: 'expired',
    dueDate: '2026-02-16',
    filledInBy: null,
    completedAt: null,
  },
  {
    id: 'obs-femke-3',
    origin: 'manager',
    status: 'pending',
    dueDate: '2026-05-24',
    filledInBy: 'Ria de Wit',
    completedAt: null,
  },
  {
    id: 'obs-femke-4',
    origin: 'group',
    status: 'todo',
    dueDate: '2026-09-21',
    filledInBy: null,
    completedAt: null,
  },
];

const femkeNotes: ReadonlyArray<ChildNote> = [
  {
    id: 'note-femke-1',
    author: 'Jasper van Dijk',
    datetime: '2026-04-03T14:00:00',
    body: 'Floris heeft vandaag veel interactie gehad met andere kinderen tijdens het spelen.\nIk zag hem lachen en proberen om contact te maken.\n\nHij is minder verlegen dan vorige week, wat een positieve ontwikkeling is. Ik ga dit gedrag verder volgen om te zien of deze sociale vaardigheden blijven groeien.',
    isObservation: false,
  },
  {
    id: 'note-femke-2',
    author: 'Lina Jansen',
    datetime: '2026-04-02T09:34:00',
    body: 'Vandaag merkte ik dat Floris zich enorm concentreert op geluiden. Hij reageert enthousiast als er muziek wordt gespeeld en kijkt geïnteresseerd naar de bron van het geluid. Het lijkt erop dat hij sneller probeert te herkennen. Dit kan een goed moment zijn om hem klein te zetten aan verschillende geluidsproblemen en muziekjes.',
    isObservation: false,
  },
  {
    id: 'note-femke-3',
    author: 'Evi de Wit',
    datetime: '2026-04-01T17:15:00',
    body: 'De afgelopen dagen valt het me op dat Floris veel met zijn handjes bezig is, vooral met het open en dicht maken van zijn vuistjes. Als hij een rammelaar probeert te pakken lukt dat soms, maar vaak glijdt het nog weg. Ik zie ook dat Floris sneller gefrustreerd raakt als het niet meteen lukt. Ik blijf dit de komende tijd observeren om te zien of dit vanzelf verbetert en of extra tips of ondersteuning helpend zijn.',
    isObservation: true,
  },
];

const jasperNotes: ReadonlyArray<ChildNote> = [
  {
    id: 'note-jasper-1',
    author: 'Lina Jansen',
    datetime: '2026-05-20T11:22:00',
    body: 'Jasper deelt vaker speelgoed met de andere kinderen en pakt steeds vaker initiatief in groepsspel. Mooi om te zien.',
    isObservation: false,
  },
  {
    id: 'note-jasper-2',
    author: 'Evi de Wit',
    datetime: '2026-05-10T08:45:00',
    body: 'Tijdens het buitenspelen klimt Jasper zelfstandig op het klimrek. Zijn grove motoriek ontwikkelt zich heel goed. Ik plan deze week een gerichte observatie van het rennen en springen.',
    isObservation: true,
  },
];

const sophieNotes: ReadonlyArray<ChildNote> = [
  {
    id: 'note-sophie-1',
    author: 'Jasper van Dijk',
    datetime: '2026-05-25T13:05:00',
    body: 'Sophie heeft vandaag een eigen verhaal verteld bij een tekening. Ze gebruikt steeds meer zinsbouw met meer dan vijf woorden.',
    isObservation: false,
  },
];

type DevelopmentProfile = 'on-track' | 'ahead' | 'behind';

const PROFILE_OFFSET: Readonly<Record<DevelopmentProfile, number>> = {
  'on-track': 0,
  ahead: 4,
  behind: -4,
};

const DOMAIN_OFFSETS: Readonly<Record<DevelopmentDomain, number>> = {
  'grove-motoriek': 2.5,
  'fijne-motoriek': 0,
  taal: -1.5,
  cognitief: -3,
  'sociaal-emotioneel': 1.5,
};

type NormBandObservation = Readonly<{ age: number; center: number }>;

// Each observation's age (years) paired with the centre of the TNO D-score
// normative band at that age, so generated observations mostly fall inside the
// development chart's band (see development-norm-band.const.ts).
const NORM_BAND_OBSERVATIONS: ReadonlyArray<NormBandObservation> = [
  { age: 0.33, center: 24.8 },
  { age: 0.83, center: 43.2 },
  { age: 1.5, center: 58.8 },
  { age: 2.33, center: 69.4 },
  { age: 3.17, center: 76.6 },
  { age: 3.83, center: 80.1 },
];

const MAX_DSCORE: number = 90;
const MS_PER_YEAR: number = 365.25 * 24 * 60 * 60 * 1000;

function ageYears(dateOfBirth: string, now: Date): number {
  const dob: Date = new Date(dateOfBirth);
  return Math.max(0, (now.getTime() - dob.getTime()) / MS_PER_YEAR);
}

function clampDScore(value: number): number {
  if (value < 0) return 0;
  if (value > MAX_DSCORE) return MAX_DSCORE;
  return value;
}

function dScoreAt(center: number, offset: number): number {
  return clampDScore(Math.round((center + offset) * 100) / 100);
}

function buildDevelopment(dateOfBirth: string, profile: DevelopmentProfile, now: Date = new Date()): ChildDevelopment {
  const currentAge: number = ageYears(dateOfBirth, now);
  const offset: number = PROFILE_OFFSET[profile];
  const observations: ReadonlyArray<ChildDevelopmentObservation> = NORM_BAND_OBSERVATIONS.filter(
    (observation: NormBandObservation): boolean => observation.age <= currentAge,
  ).map(
    ({ age, center }: NormBandObservation): ChildDevelopmentObservation => ({
      overall: { age, dScore: dScoreAt(center, offset) },
      domains: {
        'grove-motoriek': { age, dScore: dScoreAt(center, offset + DOMAIN_OFFSETS['grove-motoriek']) },
        'fijne-motoriek': { age, dScore: dScoreAt(center, offset + DOMAIN_OFFSETS['fijne-motoriek']) },
        taal: { age, dScore: dScoreAt(center, offset + DOMAIN_OFFSETS.taal) },
        cognitief: { age, dScore: dScoreAt(center, offset + DOMAIN_OFFSETS.cognitief) },
        'sociaal-emotioneel': { age, dScore: dScoreAt(center, offset + DOMAIN_OFFSETS['sociaal-emotioneel']) },
      },
    }),
  );
  return { observations };
}

const femkeObservations: ReadonlyArray<ChildDevelopmentObservation> = [
  {
    overall: { age: 0.62, dScore: 32 },
    domains: {
      'grove-motoriek': { age: 0.5, dScore: 34 },
      'fijne-motoriek': { age: 0.5, dScore: 32 },
      taal: { age: 0.5, dScore: 30.5 },
      cognitief: { age: 0.5, dScore: 29 },
      'sociaal-emotioneel': { age: 0.5, dScore: 33 },
    },
  },
  {
    overall: { age: 1.13, dScore: 48 },
    domains: {
      'grove-motoriek': { age: 1, dScore: 50 },
      'fijne-motoriek': { age: 1, dScore: 48 },
      taal: { age: 1, dScore: 46.5 },
      cognitief: { age: 1, dScore: 45 },
      'sociaal-emotioneel': { age: 1, dScore: 49 },
    },
  },
  {
    overall: { age: 1.71, dScore: 59 },
    domains: {
      'grove-motoriek': { age: 1.5, dScore: 61 },
      'fijne-motoriek': { age: 1.5, dScore: 59 },
      taal: { age: 1.5, dScore: 57.5 },
      cognitief: { age: 1.5, dScore: 56 },
      'sociaal-emotioneel': { age: 1.5, dScore: 58 },
    },
  },
];

// A child whose D-score sits exactly on the middle of the normative band at
// every age (the precise midpoint between the lower and upper bound). Drives the
// development chart's visual-regression baseline.
function averageObservation(age: number, dScore: number): ChildDevelopmentObservation {
  return {
    overall: { age, dScore },
    domains: {
      'grove-motoriek': { age, dScore },
      'fijne-motoriek': { age, dScore },
      taal: { age, dScore },
      cognitief: { age, dScore },
      'sociaal-emotioneel': { age, dScore },
    },
  };
}

const AVERAGE_OBSERVATIONS: ReadonlyArray<ChildDevelopmentObservation> = [
  averageObservation(0.5, 31.86),
  averageObservation(1.5, 58.78),
  averageObservation(2.5, 71.2),
  averageObservation(3.5, 78.42),
];

type MockChildSeed = Omit<Child, 'locations' | 'groups' | 'hidden'> & {
  location: ChildLocation;
  group: ChildGroup;
  locations?: ReadonlyArray<ChildLocation>;
  groups?: ReadonlyArray<ChildGroup>;
  hidden?: boolean;
};

const DEFAULT_CHILD_SEEDS: ReadonlyArray<MockChildSeed> = [
  {
    id: 'ch-01',
    name: 'Femke Bakker',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.vlindertjes,
    dateOfBirth: '2024-06-28',
    development: { observations: femkeObservations },
    notes: femkeNotes,
    observations: femkeObservationList,
  },
  {
    id: 'ch-02',
    name: 'Jasper van Dijk',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    locations: [LOCATIONS.kerkstraat, LOCATIONS.singel],
    group: GROUPS.bijtjes,
    groups: [GROUPS.bijtjes, GROUPS.leeuwtjes],
    dateOfBirth: '2023-02-14',
    development: buildDevelopment('2023-02-14', 'on-track'),
    notes: jasperNotes,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-03',
    name: 'Sophie de Vries',
    status: 'active',
    hidden: true,
    location: LOCATIONS.kerkstraat,
    group: GROUPS.leeuwtjes,
    dateOfBirth: '2022-11-03',
    development: buildDevelopment('2022-11-03', 'ahead'),
    notes: sophieNotes,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-04',
    name: 'Ravi van Houten',
    status: 'ended',
    location: LOCATIONS.vondelparkweg,
    group: GROUPS.katjes,
    dateOfBirth: '2022-04-21',
    development: { observations: AVERAGE_OBSERVATIONS },
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-05',
    name: 'Liam Jansen',
    status: 'active',
    location: LOCATIONS.singel,
    group: GROUPS.kikkertjes,
    dateOfBirth: '2025-01-09',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-06',
    name: 'Maya de Boer',
    status: 'active',
    location: LOCATIONS.prinsHendrikkade,
    group: GROUPS.vogeltjes,
    dateOfBirth: '2023-08-17',
    development: buildDevelopment('2023-08-17', 'behind'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-07',
    name: 'Noah van den Berg',
    status: 'active',
    location: LOCATIONS.westerstraat,
    group: GROUPS.olifantjes,
    dateOfBirth: '2022-09-30',
    development: buildDevelopment('2022-09-30', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-08',
    name: 'Emma Smit',
    status: 'active',
    location: LOCATIONS.keizersgracht,
    group: GROUPS.honden,
    dateOfBirth: '2024-12-05',
    development: buildDevelopment('2024-12-05', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-09',
    name: 'Lucas Meijer',
    status: 'active',
    location: LOCATIONS.vondelparkweg,
    group: GROUPS.katjes,
    dateOfBirth: '2023-05-22',
    development: buildDevelopment('2023-05-22', 'ahead'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-10',
    name: 'Zoe Hermans',
    status: 'active',
    location: LOCATIONS.singel,
    group: GROUPS.vlindertjes,
    dateOfBirth: '2025-03-18',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-11',
    name: 'Finn van der Linden',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.vlindertjes,
    dateOfBirth: '2024-10-11',
    development: buildDevelopment('2024-10-11', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-12',
    name: 'Lotte Koster',
    status: 'active',
    location: LOCATIONS.westerstraat,
    group: GROUPS.bijtjes,
    dateOfBirth: '2023-07-04',
    development: buildDevelopment('2023-07-04', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-13',
    name: 'Daan van Leeuwen',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.leeuwtjes,
    dateOfBirth: '2022-03-15',
    development: buildDevelopment('2022-03-15', 'behind'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-14',
    name: 'Julia Bakker',
    status: 'active',
    location: LOCATIONS.singel,
    group: GROUPS.kikkertjes,
    dateOfBirth: '2024-02-29',
    development: buildDevelopment('2024-02-29', 'ahead'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-15',
    name: 'Timo van Esch',
    status: 'active',
    location: LOCATIONS.prinsHendrikkade,
    group: GROUPS.vogeltjes,
    dateOfBirth: '2023-11-12',
    development: buildDevelopment('2023-11-12', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-16',
    name: 'Yara de Groot',
    status: 'active',
    location: LOCATIONS.keizersgracht,
    group: GROUPS.honden,
    dateOfBirth: '2025-05-02',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-17',
    name: 'Nina Brouwer',
    status: 'ended',
    location: LOCATIONS.westerstraat,
    group: GROUPS.olifantjes,
    dateOfBirth: '2022-06-19',
    development: buildDevelopment('2022-06-19', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-18',
    name: 'Olivier Smits',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.bijtjes,
    dateOfBirth: '2024-08-23',
    development: buildDevelopment('2024-08-23', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-19',
    name: 'Anna Visser',
    status: 'active',
    location: LOCATIONS.singel,
    group: GROUPS.leeuwtjes,
    dateOfBirth: '2023-01-28',
    development: buildDevelopment('2023-01-28', 'ahead'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-20',
    name: 'Pien de Wit',
    status: 'active',
    location: LOCATIONS.prinsHendrikkade,
    group: GROUPS.olifantjes,
    dateOfBirth: '2024-04-06',
    development: buildDevelopment('2024-04-06', 'on-track'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-21',
    name: 'Mees Janssen',
    status: 'active',
    location: LOCATIONS.keizersgracht,
    group: GROUPS.katjes,
    dateOfBirth: '2025-08-14',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'ch-22',
    name: 'Tess Bakker',
    status: 'active',
    location: LOCATIONS.vondelparkweg,
    group: GROUPS.honden,
    dateOfBirth: '2023-10-25',
    development: buildDevelopment('2023-10-25', 'behind'),
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
];

function seedToChild(seed: MockChildSeed): Child {
  const { location, group, locations, groups, hidden, ...rest } = seed;
  return { ...rest, hidden: hidden ?? false, locations: locations ?? [location], groups: groups ?? [group] };
}

const DEFAULT_CHILDREN: ReadonlyArray<Child> = DEFAULT_CHILD_SEEDS.map(seedToChild);

const SINGLE_PAGE_CHILDREN: ReadonlyArray<Child> = DEFAULT_CHILDREN.slice(0, 5);

// One child sits in a group that is paginated out of the group filter's first page of options
// (De Maantjes), the other in a first-page group, so filtering by De Maantjes both narrows the
// list and restores a selection that the options list does not surface until searched.
const PAGINATED_GROUP_CHILD_SEEDS: ReadonlyArray<MockChildSeed> = [
  {
    id: 'pg-01',
    name: 'Tygo Maan',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.maantjes,
    dateOfBirth: '2024-03-12',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
  {
    id: 'pg-02',
    name: 'Saar Vlinder',
    status: 'active',
    location: LOCATIONS.kerkstraat,
    group: GROUPS.vlindertjes,
    dateOfBirth: '2023-09-05',
    development: NO_DEVELOPMENT,
    notes: NO_NOTES,
    observations: NO_OBSERVATIONS,
  },
];

const PAGINATED_GROUP_CHILDREN: ReadonlyArray<Child> = PAGINATED_GROUP_CHILD_SEEDS.map(seedToChild);

export const MOCK_CHILDREN_SCENARIOS = {
  default: DEFAULT_CHILDREN,
  'single-page': SINGLE_PAGE_CHILDREN,
  'paginated-group': PAGINATED_GROUP_CHILDREN,
} as const satisfies Record<string, ReadonlyArray<Child>>;

export type MockChildrenScenarioName = keyof typeof MOCK_CHILDREN_SCENARIOS;

export function getMockChildrenScenarioName(): MockChildrenScenarioName {
  const stored: string | null = window.localStorage.getItem(MOCK_CHILDREN_SCENARIO_KEY);
  if (stored !== null && stored in MOCK_CHILDREN_SCENARIOS) {
    return stored as MockChildrenScenarioName;
  }
  return 'default';
}

const MOCK_CREATED_DUE_DAYS: Readonly<number> = 42;

let mockCreatedObservationCount: number = 0;
const createdObservations: Map<string, ChildObservation[]> = new Map<string, ChildObservation[]>();

// Created observations persist for the session so a re-fetch (getChild) returns them, like a real backend.
export function getCreatedObservations(childId: string): ReadonlyArray<ChildObservation> {
  return createdObservations.get(childId) ?? [];
}

export function buildMockCreatedObservation(childId: string): ChildObservation {
  const dueDate: Date = new Date();
  dueDate.setDate(dueDate.getDate() + MOCK_CREATED_DUE_DAYS);
  mockCreatedObservationCount += 1;
  const observation: ChildObservation = {
    id: `mock-created-${mockCreatedObservationCount}`,
    origin: 'manager',
    status: 'todo',
    dueDate: dueDate.toISOString(),
    filledInBy: null,
    completedAt: null,
  };
  createdObservations.set(childId, [...(createdObservations.get(childId) ?? []), observation]);
  return observation;
}

const CHILD_SORT_KEYS: Readonly<Record<ChildSortField, (child: Child) => string>> = {
  name: (child: Child): string => child.name,
  group: (child: Child): string => joinNames(child.groups),
};

export function applyChildrenQuery(children: ReadonlyArray<Child>, query: ChildrenQuery): ChildrenResult {
  let filtered: ReadonlyArray<Child> = children;
  if (query.locationId !== '') {
    filtered = filtered.filter((child: Child): boolean =>
      child.locations.some((location: ChildLocation): boolean => location.id === query.locationId),
    );
  }
  if (query.groupId !== '') {
    filtered = filtered.filter((child: Child): boolean =>
      child.groups.some((group: ChildGroup): boolean => group.id === query.groupId),
    );
  }
  if (query.hidden !== null) {
    filtered = filtered.filter((child: Child): boolean => child.hidden === query.hidden);
  }
  const search: string = query.search.trim().toLocaleLowerCase('nl');
  if (search !== '') {
    filtered = filtered.filter((child: Child): boolean => child.name.toLocaleLowerCase('nl').includes(search));
  }
  if (query.sortField !== null) {
    const factor: number = query.sortDirection === 'asc' ? 1 : -1;
    const sortKey: (child: Child) => string = CHILD_SORT_KEYS[query.sortField];
    filtered = [...filtered].sort(
      (first: Child, second: Child): number => factor * sortKey(first).localeCompare(sortKey(second), 'nl'),
    );
  }
  const total: number = filtered.length;
  const start: number = query.page * query.pageSize;
  return { items: filtered.slice(start, start + query.pageSize), total };
}
